import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { categoryApi } from "../../../api/categoryApi";
import { reviewDeskApi } from "../../../api/reviewDeskApi";
import { useNavigate } from "react-router-dom";
import "./ReviewByCategory.css";

const mapProgressValue = (progress, key) =>
  Number(progress?.[key] ?? progress?.[key[0].toUpperCase() + key.slice(1)] ?? 0);

const mapProgressBool = (progress, key) => {
  const value = progress?.[key] ?? progress?.[key[0].toUpperCase() + key.slice(1)];
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  if (typeof value === "number") return value > 0;
  return false;
};

const normalizeId = (value) => String(value ?? "").trim().toLowerCase();

const getCategoryId = (item) => normalizeId(item?.id ?? item?.Id ?? item?.categoryId ?? item?.CategoryId);

const extractItems = (payload) =>
  payload?.items ??
  payload?.Items ??
  payload?.data?.items ??
  payload?.data?.Items ??
  payload?.Data?.items ??
  payload?.Data?.Items ??
  [];

const extractTotalCount = (payload) =>
  Number(
    payload?.totalCount ??
    payload?.TotalCount ??
    payload?.data?.totalCount ??
    payload?.data?.TotalCount ??
    payload?.Data?.totalCount ??
    payload?.Data?.TotalCount ??
    0
  );

const parseCategoryTable = (json) => {
  return {
    items: extractItems(json),
    totalCount: extractTotalCount(json),
  };
};

export default function ReviewByCategory() {
  const [categories, setCategories] = useState([]);
  const [searchKey, setSearchKey] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("pendingDesc");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const loadProgressByCategory = useCallback(async (items) => {
    if (!items.length) return new Map();
    const categoryIds = items.map((item) => getCategoryId(item)).filter(Boolean);
    const chunkSize = 40;
    const chunks = [];
    for (let i = 0; i < categoryIds.length; i += chunkSize) {
      chunks.push(categoryIds.slice(i, i + chunkSize));
    }

    const responses = await Promise.all(
      chunks.map((chunkIds) => reviewDeskApi.getCategoryProgress(chunkIds, { includeNew: true }))
    );

    const progressItems = responses.flatMap((response) => extractItems(response));
    const map = new Map();
    progressItems.forEach((progress) => {
      const categoryId = getCategoryId(progress);
      if (!categoryId) return;
      map.set(categoryId, {
        totalCards: mapProgressValue(progress, "totalCards"),
        dueCount: mapProgressValue(progress, "dueCount"),
        newCount: mapProgressValue(progress, "newCount"),
        pendingCount: mapProgressValue(progress, "pendingCount"),
        isCompleted: mapProgressBool(progress, "isCompleted"),
      });
    });
    return map;
  }, []);

  const fetchCategoriesTable = useCallback(async (keyword = "") => {
    setIsLoading(true);
    setError("");
    try {
      const pageSize = 20;
      const params = {
        pageNumber: 1,
        pageSize,
        filters: {},
        isSortDesc: true,
        sortKey: "modifiedDate",
        seachKey: keyword,
      };

      const firstPage = await categoryApi.getTable(params);
      const firstParsed = parseCategoryTable(firstPage);
      let items = [...firstParsed.items];
      const totalCount = firstParsed.totalCount;
      const totalPages = Math.ceil(totalCount / pageSize);

      if (totalPages > 1) {
        const restPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) =>
            categoryApi.getTable({
              ...params,
              pageNumber: index + 2,
            })
          )
        );
        restPages.forEach((pageJson) => {
          const parsed = parseCategoryTable(pageJson);
          items = items.concat(parsed.items);
        });
      }

      const progressMap = await loadProgressByCategory(items);
      const enriched = items.map((item) => {
        const normalizedId = getCategoryId(item);
        const progress = progressMap.get(normalizedId) ?? {
          totalCards: 0,
          dueCount: 0,
          newCount: 0,
          pendingCount: 0,
          isCompleted: true,
        };
        const isCompleted = typeof progress.isCompleted === "boolean"
          ? progress.isCompleted
          : progress.pendingCount <= 0;
        return {
          ...item,
          id: item?.id ?? item?.Id,
          name: item?.name ?? item?.Name ?? "",
          ...progress,
          isCompleted,
        };
      });
      setCategories(enriched);
    } catch (err) {
      setError(err?.message || "Không tải được danh sách category.");
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadProgressByCategory]);

  useEffect(() => {
    fetchCategoriesTable("");
  }, [fetchCategoriesTable]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleChangeSearch = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      fetchCategoriesTable(value.trim());
    }, 300);
  };

  const handleClickCategoryItem = (item) => {
    if (item.pendingCount <= 0) return;
    navigate("/review/flashcard", {
      state: {
        id: item.id,
        name: item.name,
      },
    });
  };

  const shownCategories = useMemo(() => {
    let result = [...categories];

    if (statusFilter === "incomplete") {
      result = result.filter((item) => item.pendingCount > 0);
    }
    if (statusFilter === "completed") {
      result = result.filter((item) => item.pendingCount <= 0);
    }

    result.sort((a, b) => {
      if (sortKey === "pendingAsc") {
        return a.pendingCount - b.pendingCount || a.name.localeCompare(b.name);
      }
      if (sortKey === "nameAsc") {
        return a.name.localeCompare(b.name);
      }
      if (sortKey === "nameDesc") {
        return b.name.localeCompare(a.name);
      }
      return b.pendingCount - a.pendingCount || a.name.localeCompare(b.name);
    });

    return result;
  }, [categories, statusFilter, sortKey]);

  const incompleteCount = useMemo(
    () => categories.filter((item) => item.pendingCount > 0).length,
    [categories]
  );

  return (
    <div className="container py-4 review-category-page">
      <section className="review-category-hero mb-4">
        <div>
          <p className="review-category-kicker mb-2">Smart Practice</p>
          <h3 className="review-category-title mb-1">Review By Category</h3>
          <p className="review-category-subtitle mb-0">
            Chọn một bộ thẻ để bắt đầu phiên ôn tập theo thuật toán.
          </p>
        </div>
        <div className="review-category-badge">
          <span className="review-category-badge-label">Need Review</span>
          <strong>{incompleteCount}</strong>
        </div>
      </section>

      <section className="review-category-toolbar mb-3">
        <div className="review-search-box">
          <i className="bi bi-search"></i>
          <input
            className="form-control"
            placeholder="Tìm category..."
            value={searchKey}
            onChange={handleChangeSearch}
          />
        </div>
        <select
          className="form-select review-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tất cả</option>
          <option value="incomplete">Chưa hoàn thành</option>
          <option value="completed">Đã hoàn thành</option>
        </select>
        <select
          className="form-select review-filter-select"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
        >
          <option value="pendingDesc">Còn nhiều thẻ nhất</option>
          <option value="pendingAsc">Còn ít thẻ nhất</option>
          <option value="nameAsc">Tên A-Z</option>
          <option value="nameDesc">Tên Z-A</option>
        </select>
      </section>

      {isLoading && <div className="review-feedback">Đang tải danh sách category...</div>}
      {!isLoading && error && <div className="alert alert-danger py-2">{error}</div>}

      {!isLoading && !error && shownCategories.length === 0 && (
        <div className="review-empty-state">
          <h5 className="mb-1">Chưa có category phù hợp</h5>
          <p className="mb-0">Thử từ khóa khác hoặc tạo thêm category mới.</p>
        </div>
      )}

      {!isLoading && !error && shownCategories.length > 0 && (
        <div className="review-category-grid">
          {shownCategories.map((item, index) => {
            const reviewType = item.isCompleted
              ? "completed"
              : item.dueCount > 0 && item.newCount > 0
                ? "mixed"
                : item.dueCount > 0
                  ? "due-only"
                  : item.newCount > 0
                    ? "new-only"
                    : "pending-only";

            return (
            <button
              key={item.id}
              type="button"
              className={`review-category-card status-${reviewType}`}
              disabled={item.isCompleted}
              onClick={() => handleClickCategoryItem(item)}
            >
              <div className="review-category-card-top">
                <span className="review-category-index">#{index + 1}</span>
                <span className={`review-category-chip chip-${reviewType}`}>
                  {item.isCompleted ? "Completed" : "Need Review"}
                </span>
              </div>
              <h6 className="review-category-name">{item.name}</h6>
              <p className="review-category-desc mb-0">
                {item.isCompleted
                  ? "Không còn thẻ cần học hôm nay."
                  : `Còn ${item.pendingCount} thẻ cần học hôm nay.`}
              </p>
              <div className="review-category-stats">
                <span className={`stat-pill stat-pending ${item.pendingCount <= 0 ? "is-zero" : ""}`}>Pending: {item.pendingCount}</span>
                <span className={`stat-pill stat-due ${item.dueCount <= 0 ? "is-zero" : ""}`}>Due: {item.dueCount}</span>
                <span className={`stat-pill stat-new ${item.newCount <= 0 ? "is-zero" : ""}`}>New: {item.newCount}</span>
                <span className={`stat-pill stat-total ${item.totalCards <= 0 ? "is-zero" : ""}`}>Total: {item.totalCards}</span>
              </div>
              <span className="review-category-action">
                {item.isCompleted ? "Done Today" : "Start Review"} <i className="bi bi-arrow-right-short"></i>
              </span>
            </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
